import React, {
  useState, useCallback, forwardRef, useImperativeHandle, useRef, useEffect,
} from 'react';

import {
  Form, Input, Button, notification, Select,
} from 'antd';
import * as Yup from 'yup';

import useManager from '../../../hooks/manager';
import api from '../../../services/api';
import Modal from '../../Modal';

const { TextArea } = Input;

const UpdateForm = ({ onSuccess }, ref) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [id, setId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [appIntegrations, setAppIntegrations] = useState([]);
  const [appIntegration, setAppIntegration] = useState(null);
  const { update } = useManager();
  const formRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/appintegration');
        setAppIntegrations(data);
      } catch (err) {
        console.error('Failed to load app integrations', err);
      }
    })();
  }, []);

  const open = useCallback((openId) => {
    (async () => {
      try {
        setLoading(true);
        setShowModal(true);
        const { data } = await api.get(`/product/${openId}`);
        setId(openId);
        formRef.current.setFieldsValue({ name: data.name });
        formRef.current.setFieldsValue({ description: data.description });
        formRef.current.setFieldsValue({ slug: data.slug });
        setAppIntegration(data.appIntegration?._id || data.appIntegration);
      } catch (error) {
        console.error('Failed to load product', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useImperativeHandle(ref, () => ({
    open,
  }));

  const handleSubmit = useCallback(() => {
    (async () => {
      try {
        setLoading(true);
        const data = {
          ...formRef.current.getFieldsValue(),
          appIntegration,
        };

        const schema = Yup.object().shape({
          name: Yup.string().required('Informe o nome'),
          description: Yup.string().required('Informe a descrição'),
          appIntegration: Yup.string().required('Selecione um app de integração'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        // validation passed
        update(id, '/product', data, () => {
          formRef.current.resetFields();
          setValidationErrors({});
          setShowModal(false);
          if (onSuccess) {
            onSuccess();
          }
        });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.log(err);
        setValidationErrors({});
        // Validation failed
        if (err instanceof Yup.ValidationError) {
          notification.error({
            message: 'Falha na Validação',
          });
          const ve = {};
          err.inner.forEach((e) => {
            ve[e.path] = e.message;
          });
          setValidationErrors(ve);
          return;
        }

        if (err && err.response && err.response.data) {
          const { message } = err.response.data;
          notification.error({
            message,
          });

          return;
        }

        notification.error({
          message: 'Falha ao salvar',
        });
      }
    })();
  }, [
    update,
    id,
    appIntegration,
    onSuccess,
  ]);

  return (
    <Modal
      title="Alterar Produto"
      visible={showModal}
      loading={loading}
      footer={(
        <Button type="primary" size="large" onClick={handleSubmit}>
          Alterar
        </Button>
      )}
      onClose={() => setShowModal(false)}
    >
      <Form ref={formRef} size="large">
        <Form.Item
          name="name"
          validateStatus={validationErrors.name ? 'error' : ''}
          help={validationErrors.name}
          hasFeedback
        >
          <Input placeholder="Nome" />
        </Form.Item>
        <Form.Item
          name="description"
          validateStatus={validationErrors.description ? 'error' : ''}
          help={validationErrors.description}
          hasFeedback
        >
          <TextArea placeholder="Descrição" rows={4} />
        </Form.Item>
        <Form.Item
          name="slug"
          validateStatus={validationErrors.slug ? 'error' : ''}
          help={validationErrors.slug}
          hasFeedback
        >
          <Input placeholder="Código Nome do Produto" disabled />
        </Form.Item>
        <Form.Item
          validateStatus={validationErrors.appIntegration ? 'error' : ''}
          help={validationErrors.appIntegration}
          hasFeedback
        >
          <Select
            placeholder="App de Integração"
            value={appIntegration}
            onChange={setAppIntegration}
            disabled
          >
            {appIntegrations.map((a) => (
              <Select.Option key={a._id} value={a._id}>
                {a.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default forwardRef(UpdateForm);
